/**
 * Document response schemas — shapes returned by the /api/documents and
 * /api/document-types endpoints.
 *
 * Wire shapes:
 *   - GET    /api/documents?entity=&entityId=  → { documents: DocumentRow[] }
 *   - POST   /api/documents                    → { document: DocumentRow }
 *   - PATCH  /api/documents/:id                → { document: DocumentRow }
 *   - DELETE /api/documents/:id                → { success: true }
 *   - GET    /api/document-types?entity=       → { documentTypes: DocumentTypeRow[] }
 *   - GET    /api/documents/:id/download       → binary stream (no JSON schema)
 *
 * The download endpoint streams decrypted bytes with a Content-Disposition
 * attachment header, so it has no zod response schema here — same arrangement
 * as the resources download route.
 *
 * ⚠️ Storage internals (`storageKey`, `encryptionIv`, `encryptionTag`) are
 * deliberately ABSENT from every schema below. They never leave the server:
 * the IV and tag are half of what makes a stolen blob useless, and the storage
 * key is an internal handle with no meaning to a client.
 */
import { z } from 'zod';

/** Entity kinds a document may attach to. Mirrors the Prisma enum. */
export const DocumentEntitySchema = z.enum([
  'DRIVER',
  'VEHICLE',
  'RIDE',
  'ORGANIZATION',
  'INSTITUTION',
  'USER',
  'RECURRING_TRIP',
]);

/** Review lifecycle state. Mirrors the Prisma enum. */
export const DocumentStatusSchema = z.enum([
  'PENDING',
  'APPROVED',
  'REJECTED',
  'EXPIRED',
]);

/** One row of the admin-editable document type catalog. */
export const DocumentTypeRowSchema = z.object({
  id: z.string(),
  /** Stable machine key on system-seeded rows; null on admin-added rows. */
  key: z.string().nullable(),
  /** German label (primary locale). */
  nameDe: z.string(),
  /** English label. */
  nameEn: z.string(),
  /** Entity kind this type may attach to. */
  entity: DocumentEntitySchema,
  /** True when an expiry date is mandatory on upload. */
  requiresExpiry: z.boolean(),
  /** True when the type counts toward driver verification. */
  isRequired: z.boolean(),
  /** Display order within an entity. */
  sortOrder: z.number().int(),
});

/**
 * One stored document, as returned to a client. Dates are ISO strings; the
 * route converts the Prisma `Date` values.
 */
export const DocumentRowSchema = z.object({
  id: z.string(),
  /** Catalog row this document was filed under. */
  typeId: z.string(),
  /** Denormalized type labels + key, so a list render needs no second fetch. */
  typeKey: z.string().nullable(),
  typeNameDe: z.string(),
  typeNameEn: z.string(),
  /** Polymorphic owner. */
  entity: DocumentEntitySchema,
  entityId: z.string(),
  /** Original upload filename, used for display and the download filename. */
  fileName: z.string(),
  /** Validated MIME type of the stored file. */
  mimeType: z.string(),
  /** Plaintext size in bytes. */
  sizeBytes: z.number().int().nonnegative(),
  /** sha256 of the plaintext, hex — lets a client verify a download. */
  checksum: z.string(),
  status: DocumentStatusSchema,
  /** Issue date (date-only ISO string) or null. */
  issuedAt: z.string().nullable(),
  /** Expiry date (date-only ISO string) or null. */
  expiresAt: z.string().nullable(),
  /** Staff note — e.g. the rejection reason shown to a driver. */
  notes: z.string().nullable(),
  /**
   * Issued business number (e.g. "RE-2026-000042"). Present only on documents
   * the system generated; every uploaded document has null.
   */
  documentNumber: z.string().nullable().optional(),
  uploadedById: z.string(),
  reviewedById: z.string().nullable(),
  reviewedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

/** Response envelope from GET /api/documents. */
export const DocumentListResponseSchema = z.object({
  documents: z.array(DocumentRowSchema),
});

/** Response envelope from POST /api/documents and PATCH /api/documents/:id. */
export const DocumentResponseSchema = z.object({
  document: DocumentRowSchema,
});

/** Response envelope from DELETE /api/documents/:id. */
export const DocumentDeleteResponseSchema = z.object({
  success: z.literal(true),
});

/** Response envelope from GET /api/document-types. */
export const DocumentTypeListResponseSchema = z.object({
  documentTypes: z.array(DocumentTypeRowSchema),
});

/**
 * Per-owner document roll-up shown on a collapsed row of the Documents page.
 * Counts come from a grouped aggregate over the current page, never per row.
 */
export const DocumentOwnerSummarySchema = z.object({
  /** Documents attached to this owner (excluding soft-deleted). */
  total: z.number().int().nonnegative(),
  /** Awaiting staff review. */
  pending: z.number().int().nonnegative(),
  /** Reviewed and accepted. */
  approved: z.number().int().nonnegative(),
  /** Past their expiry date. */
  expired: z.number().int().nonnegative(),
  /** Still valid but expiring within the shared warning window. */
  expiringSoon: z.number().int().nonnegative(),
});

/**
 * One row of the Documents page: an ENTITY that documents can attach to, plus
 * its roll-up. Owners with zero documents are included — finding them is the
 * point of the page.
 */
export const DocumentOwnerRowSchema = z.object({
  id: z.string(),
  /** Primary label (person name, plate, order number, template name…). */
  title: z.string(),
  /** Secondary identity line; null when the entity has no useful second field. */
  subtitle: z.string().nullable(),
  /** The owner's own status badge, when it has one. */
  badge: z.string().nullable(),
  /** The owner's own date as an ISO string (ride pickup, template start, …). */
  entityDate: z.string().nullable(),
  documents: DocumentOwnerSummarySchema,
});

/** Response envelope from GET /api/documents/owners. */
export const DocumentOwnerListResponseSchema = z.object({
  owners: z.array(DocumentOwnerRowSchema),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  /** Total matching owners across all pages, for the pager. */
  total: z.number().int().nonnegative(),
});

export type DocumentRow = z.infer<typeof DocumentRowSchema>;
export type DocumentOwnerRow = z.infer<typeof DocumentOwnerRowSchema>;
export type DocumentOwnerSummary = z.infer<typeof DocumentOwnerSummarySchema>;
export type DocumentOwnerListResponse = z.infer<typeof DocumentOwnerListResponseSchema>;
export type DocumentTypeRow = z.infer<typeof DocumentTypeRowSchema>;
export type DocumentListResponse = z.infer<typeof DocumentListResponseSchema>;
export type DocumentResponse = z.infer<typeof DocumentResponseSchema>;
export type DocumentDeleteResponse = z.infer<typeof DocumentDeleteResponseSchema>;
export type DocumentTypeListResponse = z.infer<typeof DocumentTypeListResponseSchema>;
